import Image from 'next/image';
import { useMemo } from 'react';
import { NetworkData, NETWORKS } from 'src/common/types/network';

import { KeyValueRow, KeyValueTable, Text } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';

import { Trigger } from '../../domain';
import { AddressChip } from './components/AddressChip';

interface Props {
  trigger: Trigger;
}

export function UniswapLiquidityPoolTriggerFullDetails({ trigger }: Props) {
  const { getChainIconUrl } = useGetChainInfo();

  const formatWeight = (weight: string | number | null | undefined): string => {
    if (weight == null || weight === '') return '-';
    const numWeight = typeof weight === 'string' ? parseFloat(weight) : weight;
    const percentage = numWeight * 100;
    // Only show decimal places if they're not zero
    return `${percentage % 1 === 0 ? Math.round(percentage) : percentage.toFixed(1)}%`;
  };

  const networkDisplay = useMemo(() => {
    if (!trigger.signature) return '-';

    // Extract chainId from signature
    // Format: "SubgraphBalance-Uniswap-LiquidityV3-DistributionFormula-{address}-{chainId}"
    // OR: "SubgraphBalance-Upheaval-ActiveLiquidity-{address}-{chainId}" (5 parts for Upheaval)
    const parts = trigger.signature.split('-');
    if (parts.length < 5) return '-';

    const chainId = parseInt(parts[parts.length - 1], 10);
    const network = NETWORKS.find((n: NetworkData) => n.chainId === chainId);
    if (!network) return '-';

    const chainIconUrl = getChainIconUrl(chainId);

    return (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {chainIconUrl && <Image src={chainIconUrl} alt={network.name} width={20} height={20} />}
        <span style={{ textTransform: 'capitalize' }}>{network.name.replace(/-/g, ' ')}</span>
      </div>
    );
  }, [trigger.signature, getChainIconUrl]);

  const contractDisplay = useMemo(() => {
    if (!trigger.signature) return '-';

    // Extract pool address and chainId from signature
    // Format: "SubgraphBalance-Uniswap-LiquidityV3-DistributionFormula-{address}-{chainId}"
    // OR: "SubgraphBalance-Upheaval-ActiveLiquidity-{address}-{chainId}" (5 parts for Upheaval)
    const parts = trigger.signature.split('-');
    if (parts.length < 5) return '-';

    const address = parts[parts.length - 2];
    const chainId = parseInt(parts[parts.length - 1], 10);

    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <Text as="span" variant="body-sm" color="muted">
          Snapshots of LP holders of
        </Text>
        <AddressChip address={address} chainId={chainId} />
      </div>
    );
  }, [trigger.signature]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Executed with" value={contractDisplay} />
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow
        label="Token 0 Weight"
        value={formatWeight(trigger.context?.distributionFormulaArgs?.token0ShareWeight)}
      />
      <KeyValueRow
        label="Token 1 Weight"
        value={formatWeight(trigger.context?.distributionFormulaArgs?.token1ShareWeight)}
      />
      <KeyValueRow
        label="Liquidity Weight"
        value={formatWeight(trigger.context?.distributionFormulaArgs?.liquidityShareWeight)}
      />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
}
